import { queryOptions, useQuery } from '@tanstack/react-query'

import { settingsKeys } from './query-key-factory'

import { QueryConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { BaseService } from '@/types/service'
import { BaseUser, TechnicianFaq, TechnicianPhotos, TechnicianWorkHours } from '@/types/user'
import { getAssetSignedUrl, getSignedUrls } from '@/utils/db'

type GetProfileOptions = {
  id?: string
}

export type GetProfileResult = BaseUser & {
  work_hours: TechnicianWorkHours[]
  photos: (TechnicianPhotos & { surl?: string })[]
  faq: TechnicianFaq[]
  services: { id: BaseService['id']; name: BaseService['name']; price: BaseService['price'] }[]
}

export const getProfile = async ({ id }: GetProfileOptions): Promise<GetProfileResult> => {
  let uid = id
  if (!uid) {
    const { data: user, error: userError } = await supabase.auth.getUser()

    if (userError) {
      logger.error('Error fetching user', { errorMessage: userError })
      throw userError
    }
    if (!user.user) {
      logger.error('No user found in session')
      throw new Error('No user found in session')
    }
    uid = user.user.id
  }
  const query = supabase
    .from('users')
    .select(
      `*,
      work_hours:technician_work_hours(*),
      photos:technician_photos!technician_photos_technician_id_fkey(*),
      faq:technician_faq(*),
      services:services(id, name, price)`,
    )
    .eq('id', uid)

  const { data, error } = await query.single()

  if (error) {
    logger.error('Error fetching profile', { errorMessage: error, uid })
    throw error
  }

  // Profile picture fetch
  if (data?.picture_url) {
    data.picture_url = await getAssetSignedUrl({
      bucket: 'user_profile_pictures',
      assetName: data.picture_url,
      expiresIn: 60 * 60 * 24 * 30, // 30 days
    })
  }

  // Add signed URLs for technician photos
  if (data?.photos && data.photos.length) {
    const signedUrls = await getSignedUrls(
      data.photos.map((photo) => photo.photo_path),
      'technician_photos',
      60 * 60 * 1, // 1 hour
    )
    const photosWithSurls = data.photos.map((p, i) => ({ ...p, surl: signedUrls[i] }))
    data.photos = photosWithSurls
  }

  return data
}

export const getProfileQueryOptions = ({ id }: GetProfileOptions) => {
  return queryOptions({
    queryKey: settingsKeys.detail(id),
    queryFn: () => getProfile({ id }),
  })
}

type UseProfileOptions = {
  id?: string
  queryConfig?: QueryConfig<typeof getProfileQueryOptions>
}

export const useProfile = ({ id, queryConfig }: UseProfileOptions = {}) => {
  const config = queryConfig || {}
  return useQuery({
    ...getProfileQueryOptions({ id }),
    ...config,
  })
}
